/*
====================================================================
                  Componente NotificacionAuth
====================================================================
Este componente se encarga de mostrar notificaciones de diferentes 
tipos (verde, rojo, amarillo, azul) en respuesta a eventos de autenticación 
u otros eventos en la aplicación. Las notificaciones se muestran de manera 
transitoria y se pueden personalizar en duración, tipo y contenido.

- Utiliza `useImperativeHandle` para exponer métodos `show` y `hide`.
- Muestra diferentes tipos de notificaciones con animaciones de entrada/salida.
- Gestiona el tiempo de visibilidad de la notificación.

====================================================================
*/

// ------------------------------------------------------------------
//                            IMPORTS
// ------------------------------------------------------------------
// Importación de librerías estándar de React y componentes internos.

import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import NotificacionEntity from "../../core/entities/notificacionEntity";
import AlertaVerde from "../alertasAdvertencias/AlertaVerde";
import AlertaRojo from "../alertasAdvertencias/AlertaRojo";
import AlertaAmarillo from "../alertasAdvertencias/AlertaAmarillo";
import AlertaAzul from "../alertasAdvertencias/AlertaAzul";

// ------------------------------------------------------------------
//                       DEFINICIÓN DEL COMPONENTE
// ------------------------------------------------------------------
/**
 * Componente de notificación para autenticación.
 * 
 * Este componente muestra notificaciones flotantes en la parte inferior de la pantalla.
 * La notificación puede ser de tipo verde, rojo, amarillo o azul, dependiendo de 
 * la gravedad o el tipo de mensaje que se quiera mostrar.
 * 
 * @param {object} props
 * @param {React.Ref} ref - Referencia externa para controlar las notificaciones.
 * 
 * @returns {JSX.Element} El componente de notificación.
 */
const NotificacionAuth = forwardRef(({}, ref) => {
  const [vPosicion, setVPosicion] = useState("-20");
  const [isActivo, setIsActivo] = useState(false);
  const [notificacion, setnotificacion] = useState(new NotificacionEntity());
  var timer = null;

  const [vKeyAlerta, setvKeyAlerta] = useState(Date.now());

  /**
   * Exposición de métodos para controlar la notificación desde un componente padre.
   */
  useImperativeHandle(ref, () => ({
    /**
     * Muestra una nueva notificación.
     * 
     * @param {NotificacionEntity} newNotificacion - Objeto de notificación a mostrar.
     */
    show(newNotificacion) {
      setvKeyAlerta(Date.now());
      if (isActivo) {
        handleCerrar(false);
        setTimeout(() => {
          setnotificacion(newNotificacion);
          handleMostrar();
        }, 250);
      } else {
        setnotificacion(newNotificacion);
        handleMostrar();
      }
    },
    /**
     * Oculta la notificación actual.
     */
    hide() {
      handleCerrar();
    },
  }));

  /**
   * Controla la animación de entrada/salida de la notificación según su estado activo.
   */
  useEffect(() => {
    isActivo ? setVPosicion("5") : setVPosicion("-20");
  }, [isActivo]);

  /**
   * Gestiona el temporizador para ocultar la notificación después de un tiempo.
   */
  useEffect(() => {
    clearTimeout(timer);
    if (isActivo && notificacion.duracion > 0) {
      timer = setTimeout(() => {
        handleCerrar();
      }, notificacion.duracion * 1000);
      return () => clearTimeout(timer); // Limpia el temporizador cuando el componente se desmonte
    }
  }, [vKeyAlerta, notificacion.duracion]);

  /**
   * Muestra la notificación activando el estado correspondiente.
   */
  const handleMostrar = () => {
    setIsActivo(true);
    setVPosicion("5");
  };

  /**
   * Cierra la notificación y opcionalmente la elimina.
   * 
   * @param {boolean} isEliminar - Indica si se debe eliminar la notificación tras cerrar.
   */
  const handleCerrar = (isEliminar = true) => {
    if (isEliminar) {
      setTimeout(() => {
        setIsActivo(false);
      }, 250);
    }
    setVPosicion("-20");
  };

  /**
   * Selecciona el componente de alerta según el tipo de notificación.
   */
  const AlertaSeleccionada = {
    verde: (
      <AlertaVerde
        titulo={notificacion.titulo}
        descripcion={notificacion.descripcion}
        handleCerrar={handleCerrar}
        tiempo={notificacion.duracion}
        key={vKeyAlerta}
      />
    ),
    rojo: (
      <AlertaRojo
        titulo={notificacion.titulo}
        descripcion={notificacion.descripcion}
        handleCerrar={handleCerrar}
        tiempo={notificacion.duracion}
        key={vKeyAlerta}
      />
    ),
    amarillo: (
      <AlertaAmarillo
        titulo={notificacion.titulo}
        descripcion={notificacion.descripcion}
        handleCerrar={handleCerrar}
        tiempo={notificacion.duracion}
        key={vKeyAlerta}
      />
    ),
    azul: (
      <AlertaAzul
        titulo={notificacion.titulo}
        descripcion={notificacion.descripcion}
        handleCerrar={handleCerrar}
        tiempo={notificacion.duracion}
        key={vKeyAlerta}
      />
    ),
  };

  return (
    <div
      className="skillwizard-notification-auth"
      style={{ bottom: `${vPosicion}%` }}
    >
      {isActivo && AlertaSeleccionada[notificacion.tipo]}
    </div>
  );
});

// ------------------------------------------------------------------
//                            EXPORTS
// ------------------------------------------------------------------
// Exportación del componente para su uso en otros módulos de la aplicación.

export default NotificacionAuth;
